import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import type { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { TabHeader } from "@/components/base/tab-header";
import { EmptyValue } from "@/components/common";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import {
	type OrganizationUserDisplay,
	organizationQueryOptions,
} from "@/services/organization";
import { getUserInitials } from "@/utils/get-user-initials";
import { statusColors } from "../../../users/(org-users)/$orgUserId";
import { SearchSchema } from "../$subOrgId";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId/users",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

// Avatar cell component - extracted to properly use hooks
function UserAvatarCell({ user }: { user: OrganizationUserDisplay }) {
	const { src: userAvatarSrc, isLoading } = useUserAvatarSrc(
		user.userId,
		user.hasAvatar,
	);

	const firstName = user.firstName;
	const lastName = user.lastName;
	const email = user.email;

	// Only show initials when not loading and no avatar
	const initials =
		!isLoading && !userAvatarSrc
			? getUserInitials(firstName, lastName)
			: undefined;

	return (
		<div className="flex items-center gap-3">
			<Avatar
				src={userAvatarSrc || undefined}
				initials={initials}
				alt={user.displayName ?? undefined}
				size="md"
			/>
			<div className="whitespace-nowrap">
				<p className="text-sm font-medium text-primary">
					{user.displayName && user.displayName.length > 0 ? (
						user.displayName
					) : firstName &&
						firstName.length > 0 &&
						lastName &&
						lastName.length > 0 ? (
						`${firstName} ${lastName}`
					) : (
						<EmptyValue />
					)}
				</p>
				<p className="text-sm text-tertiary">{email || <EmptyValue />}</p>
			</div>
		</div>
	);
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId/users",
	});

	const usersBySubOrg = useQuery({
		...organizationQueryOptions.users.list(params.orgId, 1, 100, {
			SubOrganizationIds: params.subOrgId,
		}),
	});

	const userColumns: ColumnDef<OrganizationUserDisplay>[] = [
		{
			id: "nameEmail",
			accessorKey: "nameEmail",
			header: t(
				"org-admin.sub-organizations.tabs.users.columns.userNameAndEmail",
			),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 300,
			cell: ({ row }) => <UserAvatarCell user={row.original} />,
		},
		{
			id: "UserRoles.OrganizationRole.Name",
			accessorKey: "UserRoles.OrganizationRole.Name",
			header: t("org-admin.sub-organizations.tabs.users.columns.role"),
			minSize: 200,
			enableSorting: true,
			cell: ({ row }) => {
				const user = row.original;
				return (
					<>
						{user?.organizationRoles && user?.organizationRoles?.length > 0 ? (
							<span className="text-sm font-medium text-gray-900">
								{user.organizationRoles[0]?.name}
							</span>
						) : (
							<EmptyValue />
						)}
					</>
				);
			},
		},
		{
			id: "status.name",
			accessorKey: "status.name",
			header: t(
				"org-admin.sub-organizations.tabs.users.columns.orgMemberStatus",
			),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				const statusName = row.original.status?.name ?? "";
				const color =
					statusColors[statusName as keyof typeof statusColors] ?? "gray";
				return statusName ? (
					<BadgeWithDot type="modern" size="md" color={color}>
						{statusName}
					</BadgeWithDot>
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const data = usersBySubOrg.data ?? [];

	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});

	const pageRowCount = useMemo(() => {
		const start = pagination.pageIndex * pagination.pageSize;
		return Math.min(pagination.pageSize, Math.max(0, data.length - start));
	}, [data.length, pagination.pageIndex, pagination.pageSize]);

	const hideTableCard = !usersBySubOrg.isLoading && data.length === 0;

	const handleRowClick = (user: OrganizationUserDisplay) => {
		navigate({
			to: "/org-admin/$orgId/users/$orgUserId/$userId/details",
			params: { orgId: params.orgId, orgUserId: user.id, userId: user.userId },
			search: {
				mode: "view",
				tab: "details",
			},
		});
	};

	return (
		<div>
			<TabHeader
				title={t("org-admin.sub-organizations.tabs.users.title")}
				descriptionText={t(
					"org-admin.sub-organizations.tabs.users.description",
				)}
			/>

			<CustomTableWrapper
				data={data}
				columns={userColumns}
				isLoading={usersBySubOrg.isLoading}
				isFetching={usersBySubOrg.isFetching}
				onRowClick={handleRowClick}
				enableSorting={true}
				paginationMode="client"
				externalPaginationState={pagination}
				onPaginationChange={setPagination}
				rowCount={pageRowCount}
				totalRowCount={data.length}
				className="overflow-x-auto overflow-y-visible"
				cardClassName={hideTableCard ? "hidden" : undefined}
				showHeaderPadding={false}
				emptyState={
					<div className="flex flex-col items-center justify-center py-12">
						<p className="text-sm text-tertiary">
							{t("org-admin.sub-organizations.tabs.users.empty")}
						</p>
					</div>
				}
				zebraStriping
			/>
		</div>
	);
}
